import * as THREE from 'three/webgpu';
import {
	SketchEllipseControlPointFactory,
	SketchEllipseObject,
} from '../../../../mesh/sketch';
import type {
	SketchEllipseDefinition,
	SketchGroup,
} from '../../../../mesh/sketch';
import type { EllipseReferenceAxisMarkers } from './EllipseReferenceBuilder';
import type {
	EllipseArcReferenceBuilderInput,
} from '../../../../../types/reference/sketch-entity-reference/EllipseArcReferenceBuilder';

export type {
	EllipseArcReferenceBuilderInput,
} from '../../../../../types/reference/sketch-entity-reference/EllipseArcReferenceBuilder';

/**
 * 椭圆弧引用构建器。
 * 说明：
 * 1. 当前分支的 `SketchEllipseObject` 直接使用 start/end 控制点表达曲线端点；
 * 2. 因此这里不能照搬旧链路的“占位长短轴点”做法，必须传入真实弧端点；
 * 3. 同时仍然补齐 4 个 cardinal 标记点，为 Task 5 的关系恢复保留承接面。
 */
export class EllipseArcReferenceBuilder {
	/**
	 * 构建草图椭圆弧对象。
	 *
	 * @param input 构建输入。
	 * @returns 已完成几何初始化的椭圆弧对象。
	 */
	public build(input: EllipseArcReferenceBuilderInput): SketchEllipseObject {
		const definition: SketchEllipseDefinition = {
			centerWorld: new THREE.Vector3(...input.geometryPayload.centerWorld),
			radiusX: input.geometryPayload.radiusX,
			radiusY: input.geometryPayload.radiusY,
			rotation: input.geometryPayload.rotation,
			startAngle: input.geometryPayload.startAngle,
			endAngle: input.geometryPayload.endAngle,
			clockwise: input.geometryPayload.clockwise,
		};
		const controlPointFactory = new SketchEllipseControlPointFactory(input.targetSketch.referencePlane);
		const startPoint = controlPointFactory.createControlPoint(
			resolveEllipseWorldPointByParameter(definition, definition.startAngle, input.targetSketch),
		);
		const endPoint = controlPointFactory.createControlPoint(
			resolveEllipseWorldPointByParameter(definition, definition.endAngle, input.targetSketch),
		);
		const centerPoint = controlPointFactory.createControlPoint(definition.centerWorld);
		const axisMarkers: EllipseReferenceAxisMarkers = controlPointFactory.createAxisControlPoints(definition);
		const ellipseArc = new SketchEllipseObject({
			referencePlane: input.targetSketch.referencePlane,
			definition,
			startPoint,
			endPoint,
			centerPoint,
		});

		ellipseArc.updateEllipse();
		controlPointFactory.registerCardinalControlPoints(ellipseArc, {
			centerPoint,
			axisControlPoints: axisMarkers,
		});

		Reflect.set(ellipseArc, '__referenceAxisMarkers', axisMarkers);

		return ellipseArc;
	}
}

/**
 * 计算椭圆参数点在目标草图平面上的世界坐标。
 *
 * @param definition 椭圆定义。
 * @param parameter 参数角。
 * @param targetSketch 目标草图。
 * @returns 世界坐标点。
 */
function resolveEllipseWorldPointByParameter(
	definition: SketchEllipseDefinition,
	parameter: number,
	targetSketch: SketchGroup,
): THREE.Vector3 {
	const localCenter = targetSketch.referencePlane.worldToLocal(definition.centerWorld);
	const localPoint = new THREE.Vector2(
		localCenter.x + definition.radiusX * Math.cos(parameter),
		localCenter.y + definition.radiusY * Math.sin(parameter),
	).rotateAround(localCenter, definition.rotation);
	return targetSketch.referencePlane.localToWorld(localPoint);
}
